/**
 * SimulationProgress - Animated simulation visualization
 *
 * Shows power flowing through the system during simulation:
 * - Animated Single Line Diagram with pulsing power flows
 * - Time-lapse energy chart that draws left-to-right
 */

import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';
import { alpha, keyframes } from '@mui/material/styles';
import {
    AreaChart, Area, XAxis, YAxis, ResponsiveContainer,
} from 'recharts';

// === Design Tokens ===
const COLORS = {
    accent: '#9b59b6',
    efficiency: '#00ff88',
    solar: '#ffaa00',
    ev: '#00d4aa',
    grid: '#4488ff',
    bess: '#aa88ff',
    bessCharge: '#2ecc71',    // Green for charging
    bessDischarge: '#f39c12', // Orange for discharging
    soc: '#00ff88',           // Green for state of charge
    bg: {
        primary: 'rgba(8, 8, 12, 0.95)',
        card: 'rgba(20, 20, 32, 0.85)',
    },
    text: {
        primary: '#e8e4dc',
        secondary: '#888888',
        muted: '#555555',
    }
};

// === Animations ===
const flowPulse = keyframes`
    0% { opacity: 0.3; }
    50% { opacity: 1; }
    100% { opacity: 0.3; }
`;

const dotFlow = keyframes`
    0% { stroke-dashoffset: 16; }
    100% { stroke-dashoffset: 0; }
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

// Animated Mini Single Line Diagram
const AnimatedSLD = ({ currentHour, solarKw, evKw, gridKw, bessKw, bessCharging, soc }) => {
    const lineWidth = 2;
    const busColor = '#4ecdc4';
    const bessColor = bessKw > 0 ? (bessCharging ? COLORS.bessCharge : COLORS.bessDischarge) : COLORS.bess;

    return (
        <Box sx={{
            width: '100%',
            height: 180,
            position: 'relative',
            animation: `${fadeIn} 0.5s ease-out`,
        }}>
            <svg width="100%" height="100%" viewBox="0 0 400 160" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <filter id="animGlow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="2" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Main AC Bus */}
                <rect x="30" y="75" width="340" height="8" fill={busColor} rx="2" filter="url(#animGlow)" />
                <text x="200" y="70" fill={busColor} fontSize="9" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="600">
                    480V AC BUS
                </text>

                {/* === UTILITY GRID === */}
                <g>
                    <circle cx="60" cy="30" r="16" fill="none" stroke={COLORS.grid} strokeWidth="2" filter="url(#animGlow)" />
                    <line x1="50" y1="26" x2="70" y2="26" stroke={COLORS.grid} strokeWidth="1.5" />
                    <line x1="50" y1="30" x2="70" y2="30" stroke={COLORS.grid} strokeWidth="1.5" />
                    <line x1="50" y1="34" x2="70" y2="34" stroke={COLORS.grid} strokeWidth="1.5" />
                    <text x="60" y="12" fill={COLORS.grid} fontSize="8" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="600">GRID</text>

                    {/* Connection to bus */}
                    <line x1="60" y1="46" x2="60" y2="75" stroke={COLORS.grid} strokeWidth={lineWidth} />

                    {/* Power value */}
                    <text x="78" y="62" fill={COLORS.text.secondary} fontSize="8" fontFamily={vortxTypography.mono}>
                        {gridKw.toFixed(0)} kW
                    </text>

                    {/* Flow animation */}
                    {gridKw > 0 && (
                        <line
                            x1="60" y1="46" x2="60" y2="75"
                            stroke={COLORS.grid}
                            strokeWidth="4"
                            strokeDasharray="4 12"
                            strokeLinecap="round"
                            style={{
                                animation: `${dotFlow} 0.8s linear infinite`,
                            }}
                        />
                    )}
                </g>

                {/* === SOLAR PV === */}
                <g>
                    <rect x="120" y="18" width="30" height="22" rx="2" fill="none" stroke={COLORS.solar} strokeWidth="2" filter="url(#animGlow)" />
                    <line x1="120" y1="18" x2="150" y2="40" stroke={COLORS.solar} strokeWidth="1" />
                    <text x="135" y="8" fill={COLORS.solar} fontSize="8" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="600">PV</text>

                    {/* Connection to bus */}
                    <line x1="135" y1="40" x2="135" y2="75" stroke={COLORS.solar} strokeWidth={lineWidth} />

                    {/* Power value */}
                    <text x="153" y="58" fill={COLORS.text.secondary} fontSize="8" fontFamily={vortxTypography.mono}>
                        {solarKw.toFixed(0)} kW
                    </text>

                    {/* Flow animation */}
                    {solarKw > 0 && (
                        <line
                            x1="135" y1="40" x2="135" y2="75"
                            stroke={COLORS.solar}
                            strokeWidth="4"
                            strokeDasharray="4 12"
                            strokeLinecap="round"
                            style={{
                                animation: `${dotFlow} 0.6s linear infinite`,
                            }}
                        />
                    )}
                </g>

                {/* === BESS === */}
                <g>
                    {/* Battery symbol - color changes based on charge/discharge */}
                    <rect
                        x="195" y="100" width="30" height="35" rx="2"
                        fill="none"
                        stroke={bessKw > 0 ? (bessCharging ? COLORS.bessCharge : COLORS.bessDischarge) : COLORS.bess}
                        strokeWidth="2"
                        filter="url(#animGlow)"
                    />
                    <rect
                        x="205" y="96" width="10" height="4"
                        fill={bessKw > 0 ? (bessCharging ? COLORS.bessCharge : COLORS.bessDischarge) : COLORS.bess}
                    />
                    <text
                        x="210" y="122"
                        fill={bessKw > 0 ? (bessCharging ? COLORS.bessCharge : COLORS.bessDischarge) : COLORS.bess}
                        fontSize="7" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="600"
                    >
                        BESS
                    </text>

                    {/* Connection to bus */}
                    <line
                        x1="210" y1="83" x2="210" y2="100"
                        stroke={bessKw > 0 ? (bessCharging ? COLORS.bessCharge : COLORS.bessDischarge) : COLORS.bess}
                        strokeWidth={lineWidth}
                    />

                    {/* Power value with charge/discharge indicator */}
                    <text x="228" y="92" fill={COLORS.text.secondary} fontSize="8" fontFamily={vortxTypography.mono}>
                        {bessCharging ? '↓' : '↑'} {bessKw.toFixed(0)} kW
                    </text>

                    {/* Bidirectional flow animation - Green for charging (down), Orange for discharging (up) */}
                    {bessKw > 0 && (
                        <line
                            x1="210" y1={bessCharging ? 83 : 100}
                            x2="210" y2={bessCharging ? 100 : 83}
                            stroke={bessCharging ? COLORS.bessCharge : COLORS.bessDischarge}
                            strokeWidth="4"
                            strokeDasharray="4 12"
                            strokeLinecap="round"
                            style={{
                                animation: `${dotFlow} 1s linear infinite`,
                            }}
                        />
                    )}
                </g>

                {/* === EV CHARGERS === */}
                <g>
                    <rect x="275" y="100" width="30" height="35" rx="2" fill="none" stroke={COLORS.ev} strokeWidth="2" filter="url(#animGlow)" />
                    {/* Plug prongs */}
                    <line x1="283" y1="112" x2="283" y2="125" stroke={COLORS.ev} strokeWidth="3" strokeLinecap="round" />
                    <line x1="297" y1="112" x2="297" y2="125" stroke={COLORS.ev} strokeWidth="3" strokeLinecap="round" />
                    <text x="290" y="150" fill={COLORS.ev} fontSize="7" fontFamily={vortxTypography.mono} textAnchor="middle" fontWeight="600">EV</text>

                    {/* Connection to bus */}
                    <line x1="290" y1="83" x2="290" y2="100" stroke={COLORS.ev} strokeWidth={lineWidth} />

                    {/* Power value */}
                    <text x="308" y="92" fill={COLORS.text.secondary} fontSize="8" fontFamily={vortxTypography.mono}>
                        {evKw.toFixed(0)} kW
                    </text>

                    {/* Flow animation */}
                    {evKw > 0 && (
                        <line
                            x1="290" y1="83" x2="290" y2="100"
                            stroke={COLORS.ev}
                            strokeWidth="4"
                            strokeDasharray="4 12"
                            strokeLinecap="round"
                            style={{
                                animation: `${dotFlow} 0.7s linear infinite`,
                            }}
                        />
                    )}
                </g>

                {/* === AUX LOAD === */}
                <g>
                    <path
                        d="M340,100 L340,108 L332,113 L348,123 L332,133 L340,138"
                        fill="none"
                        stroke={COLORS.text.secondary}
                        strokeWidth="2"
                    />
                    <text x="340" y="150" fill={COLORS.text.muted} fontSize="7" fontFamily={vortxTypography.mono} textAnchor="middle">AUX</text>

                    {/* Connection to bus */}
                    <line x1="340" y1="83" x2="340" y2="100" stroke={COLORS.text.secondary} strokeWidth={lineWidth} />
                </g>

                {/* Connection dots on bus */}
                <circle cx="60" cy="79" r="3" fill={COLORS.grid} />
                <circle cx="135" cy="79" r="3" fill={COLORS.solar} />
                <circle cx="210" cy="79" r="3" fill={bessColor} />
                <circle cx="290" cy="79" r="3" fill={COLORS.ev} />
                <circle cx="340" cy="79" r="3" fill={COLORS.text.secondary} />
            </svg>
        </Box>
    );
};

// Time-lapse Energy Chart - Shows PV, Battery SoC, and Grid
const TimeLapseChart = ({ data, currentIndex }) => {
    // Only show data up to currentIndex
    const visibleData = data.slice(0, currentIndex + 1);

    return (
        <Box sx={{ height: 160, mt: 2 }}>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={visibleData} margin={{ top: 5, right: 45, left: 0, bottom: 0 }}>
                    <defs>
                        <linearGradient id="simGradSolar" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={COLORS.solar} stopOpacity={0.6} />
                            <stop offset="100%" stopColor={COLORS.solar} stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="simGradGrid" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={COLORS.grid} stopOpacity={0.5} />
                            <stop offset="100%" stopColor={COLORS.grid} stopOpacity={0.05} />
                        </linearGradient>
                    </defs>
                    <XAxis
                        dataKey="hour"
                        tick={{ fill: COLORS.text.muted, fontSize: 9, fontFamily: vortxTypography.mono }}
                        axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                        tickLine={false}
                        interval={5}
                        domain={[0, 24]}
                    />
                    {/* Left Y-axis for Power (kW) */}
                    <YAxis
                        yAxisId="power"
                        tick={{ fill: COLORS.text.muted, fontSize: 9, fontFamily: vortxTypography.mono }}
                        axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                        tickLine={false}
                        width={35}
                        label={{ value: 'kW', angle: -90, position: 'insideLeft', fill: COLORS.text.muted, fontSize: 8 }}
                    />
                    {/* Right Y-axis for SoC (%) */}
                    <YAxis
                        yAxisId="soc"
                        orientation="right"
                        domain={[0, 100]}
                        tick={{ fill: COLORS.soc, fontSize: 9, fontFamily: vortxTypography.mono }}
                        axisLine={{ stroke: COLORS.soc, strokeOpacity: 0.3 }}
                        tickLine={false}
                        width={35}
                        label={{ value: 'SoC %', angle: 90, position: 'insideRight', fill: COLORS.soc, fontSize: 8 }}
                    />
                    {/* Solar Generation */}
                    <Area
                        yAxisId="power"
                        type="monotone"
                        dataKey="solar"
                        stroke={COLORS.solar}
                        fill="url(#simGradSolar)"
                        strokeWidth={2}
                        isAnimationActive={false}
                    />
                    {/* Grid Import/Export */}
                    <Area
                        yAxisId="power"
                        type="monotone"
                        dataKey="grid"
                        stroke={COLORS.grid}
                        fill="url(#simGradGrid)"
                        strokeWidth={2}
                        isAnimationActive={false}
                    />
                    {/* Battery State of Charge - Line only, no fill */}
                    <Area
                        yAxisId="soc"
                        type="monotone"
                        dataKey="soc"
                        stroke={COLORS.soc}
                        fill="none"
                        strokeWidth={2}
                        strokeDasharray="4 2"
                        isAnimationActive={false}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </Box>
    );
};

// Main SimulationProgress Component
const SimulationProgress = ({ isRunning, progress, status, equipment }) => {
    const [currentHour, setCurrentHour] = useState(0);
    const [chartData, setChartData] = useState([]);
    const [currentPower, setCurrentPower] = useState({
        solar: 0,
        ev: 0,
        grid: 0,
        bess: 0,
        bessCharging: true,
    });

    const animationRef = useRef(null);

    // Track battery state of charge
    const [batterySoc, setBatterySoc] = useState(50); // Start at 50%

    // Generate simulated power values based on hour
    const getPowerForHour = (hour, eq, currentSoc) => {
        // Solar bell curve (peaks at noon)
        let solar = 0;
        if (hour >= 6 && hour <= 18) {
            const factor = Math.exp(-Math.pow(hour - 12, 2) / 18);
            solar = (eq.pv_kw || 100) * factor;
        }

        // EV load (commercial profile)
        let ev = (eq.charger_count || 4) * (eq.charger_kw || 50) * 0.1; // baseline
        if (hour >= 9 && hour <= 17) {
            ev = (eq.charger_count || 4) * (eq.charger_kw || 50) * 0.6;
        } else if (hour >= 17 && hour <= 19) {
            ev = (eq.charger_count || 4) * (eq.charger_kw || 50) * 0.4;
        }

        // Grid fills the gap or BESS provides
        const deficit = ev - solar;
        let grid = 0;
        let bess = 0;
        let bessCharging = true;
        let newSoc = currentSoc;

        const bessKwh = eq.bess_kwh || 200;
        const bessKw = eq.bess_kw || 50;

        if (deficit > 0) {
            // Need power - discharge BESS first (if SoC > 20%), then grid
            const availableBessKw = currentSoc > 20 ? Math.min(deficit, bessKw) : 0;
            bess = availableBessKw;
            bessCharging = false;
            grid = Math.max(0, deficit - bess);

            // Decrease SoC (kW * 0.5hr / kWh capacity * 100%)
            if (bess > 0) {
                newSoc = Math.max(10, currentSoc - (bess * 0.5 / bessKwh * 100));
            }
        } else {
            // Excess solar - charge BESS (if SoC < 95%)
            const chargeable = currentSoc < 95 ? Math.min(Math.abs(deficit), bessKw) : 0;
            bess = chargeable;
            bessCharging = true;

            // Increase SoC
            if (bess > 0) {
                newSoc = Math.min(100, currentSoc + (bess * 0.5 / bessKwh * 100));
            }
        }

        return { solar, ev, grid, bess, bessCharging, soc: newSoc };
    };

    // Animate through hours
    useEffect(() => {
        if (!isRunning) {
            setCurrentHour(0);
            setChartData([]);
            setBatterySoc(50); // Reset SoC
            return;
        }

        let currentSoc = 50; // Local tracking for interval

        const interval = setInterval(() => {
            setCurrentHour(prev => {
                const next = prev + 0.5; // Half-hour increments for smoother animation
                if (next >= 24) {
                    return 24;
                }

                // Update power values with SoC tracking
                const power = getPowerForHour(next, equipment, currentSoc);
                currentSoc = power.soc; // Update local SoC
                setBatterySoc(power.soc);
                setCurrentPower(power);

                // Add to chart data
                if (next % 1 === 0) { // Only add full hours to chart
                    setChartData(prevData => [
                        ...prevData,
                        {
                            hour: `${Math.floor(next)}:00`,
                            solar: power.solar,
                            grid: power.grid,
                            soc: power.soc,
                        }
                    ]);
                }

                return next;
            });
        }, 100); // Update every 100ms for smooth animation

        return () => clearInterval(interval);
    }, [isRunning, equipment]);

    // Initialize chart data
    useEffect(() => {
        if (isRunning && chartData.length === 0) {
            // Start with initial data point at hour 0
            const initialPower = getPowerForHour(0, equipment, 50);
            setChartData([{
                hour: '0:00',
                solar: initialPower.solar,
                grid: initialPower.grid,
                soc: 50, // Initial SoC
            }]);
        }
    }, [isRunning, equipment]);

    if (!isRunning) return null;

    return (
        <Box sx={{
            p: 3,
            bgcolor: COLORS.bg.card,
            borderRadius: 2,
            border: `1px solid ${alpha(COLORS.accent, 0.3)}`,
            mt: 3,
        }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.75rem',
                    color: COLORS.text.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                }}>
                    Simulation Running
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.heading,
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: COLORS.accent,
                    }}>
                        Hour {Math.floor(currentHour)} of 24
                    </Typography>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.85rem',
                        color: COLORS.efficiency,
                    }}>
                        {progress}%
                    </Typography>
                </Box>
            </Box>

            {/* Progress bar */}
            <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: alpha(COLORS.accent, 0.2),
                    mb: 2,
                    '& .MuiLinearProgress-bar': {
                        bgcolor: COLORS.accent,
                        boxShadow: `0 0 10px ${alpha(COLORS.accent, 0.5)}`,
                    },
                }}
            />

            {/* Status */}
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
                color: COLORS.text.secondary,
                mb: 2,
            }}>
                {status}
            </Typography>

            {/* Animated SLD */}
            <AnimatedSLD
                currentHour={currentHour}
                solarKw={currentPower.solar}
                evKw={currentPower.ev}
                gridKw={currentPower.grid}
                bessKw={currentPower.bess}
                bessCharging={currentPower.bessCharging}
                soc={batterySoc}
            />

            {/* Time-lapse Chart */}
            <Box sx={{ mt: 2 }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    color: COLORS.text.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    mb: 1,
                }}>
                    Energy Profile
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: COLORS.solar }} />
                        <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.65rem', color: COLORS.text.muted }}>
                            PV Generation
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: COLORS.grid }} />
                        <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.65rem', color: COLORS.text.muted }}>
                            Grid Import
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 14, height: 2, bgcolor: COLORS.soc, borderRadius: 1 }} />
                        <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.65rem', color: COLORS.soc }}>
                            Battery SoC %
                        </Typography>
                    </Box>
                </Box>
                <TimeLapseChart data={chartData} currentIndex={chartData.length - 1} />
            </Box>
        </Box>
    );
};

export default SimulationProgress;
